import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import API from '../services/api';

function AdminSubscriptions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [deleting, setDeleting] = useState(null);

  useEffect(() => { loadSubscriptions(); }, []);

  const loadSubscriptions = async () => {
    try {
      const response = await API.get('/admin/subscriptions');
      setData(response.data.data);
    } catch (error) {
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const showDeleteConfirmation = (orgName) => {
    return new Promise((resolve) => {
      const modal = document.createElement('div');
      modal.className = 'delete-confirm-overlay';
      modal.innerHTML = `
        <div class="delete-confirm-modal">
          <div class="delete-confirm-header" style="background:linear-gradient(135deg,#ef4444,#dc2626)">
            <div class="delete-confirm-icon-wrapper">⚠️</div>
            <div>
              <h3 class="delete-confirm-title">Delete Subscription?</h3>
              <p class="delete-confirm-subtitle">This action cannot be undone</p>
            </div>
          </div>
          <div class="delete-confirm-body">
            <p class="delete-confirm-desc">Organization: <strong>${orgName}</strong></p>
            <div class="delete-confirm-items">
              <div class="delete-confirm-item"><span>✓</span><span>Delete all payment records</span></div>
              <div class="delete-confirm-item"><span>✓</span><span>Remove subscription access immediately</span></div>
              <div class="delete-confirm-item"><span>✓</span><span>User data remains intact</span></div>
            </div>
            <div class="delete-confirm-actions">
              <button id="cancel-delete" class="delete-confirm-btn-cancel">Cancel</button>
              <button id="confirm-delete" class="delete-confirm-btn-confirm">🗑️ Delete Subscription</button>
            </div>
          </div>
        </div>`;
      document.body.appendChild(modal);
      modal.querySelector('#confirm-delete').onclick = () => { document.body.removeChild(modal); resolve(true); };
      modal.querySelector('#cancel-delete').onclick  = () => { document.body.removeChild(modal); resolve(false); };
      modal.onclick = (e) => { if (e.target === modal) { document.body.removeChild(modal); resolve(false); } };
    });
  };

  const handleDelete = async (subscriptionId, orgName) => {
    const confirmed = await showDeleteConfirmation(orgName);
    if (!confirmed) return;
    setDeleting(subscriptionId);
    const toastId = toast.loading('Deleting subscription...');
    try {
      await API.delete(`/admin/subscription/${subscriptionId}`);
      toast.success('Subscription deleted!', { id: toastId });
      await loadSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete', { id: toastId });
    } finally {
      setDeleting(null);
    }
  };

  if (loading) return (
    <div className="loading-container">
      <div className="loading-content">
        <div className="loading-spinner">⏳</div>
        <p className="loading-text">Loading subscriptions...</p>
      </div>
    </div>
  );

  const filteredData = () => {
    if (!data) return [];
    const all = [...data.active, ...data.noSubscription];
    if (filter === 'all')      return all;
    if (filter === 'none')     return data.noSubscription;
    if (filter === 'active')   return data.active.filter(s => s.validity_status === 'active');
    if (filter === 'expiring') return data.active.filter(s => s.validity_status === 'expiring_soon');
    if (filter === 'expired')  return data.active.filter(s => s.validity_status === 'expired');
    return all;
  };

  const stats = {
    total:    data ? data.active.length + data.noSubscription.length : 0,
    active:   data ? data.active.filter(s => s.validity_status === 'active').length : 0,
    expiring: data ? data.active.filter(s => s.validity_status === 'expiring_soon').length : 0,
    expired:  data ? data.active.filter(s => s.validity_status === 'expired').length : 0,
    noSub:    data ? data.noSubscription.length : 0,
  };

  const FILTERS = [
    { key: 'all',      label: 'All' },
    { key: 'active',   label: 'Active' },
    { key: 'expiring', label: 'Expiring' },
    { key: 'expired',  label: 'Expired' },
    { key: 'none',     label: 'No Subscription' },
  ];

  const getValidityBadge = (item) => {
    if (!item.subscription_id)           return <span className="adm-badge adm-badge-neutral">NO SUB</span>;
    if (item.validity_status === 'active')         return <span className="adm-badge adm-badge-completed">✅ ACTIVE</span>;
    if (item.validity_status === 'expiring_soon')  return <span className="adm-badge adm-badge-pending">⚠️ EXPIRING</span>;
    if (item.validity_status === 'expired')        return <span className="adm-badge adm-badge-rejected">❌ EXPIRED</span>;
    return null;
  };

  return (
    <div className="adm-page">

      {/* Header */}
      <div className="adm-header">
        <div>
          <h1 className="adm-page-title">💳 Subscriptions</h1>
          <p className="adm-page-subtitle">Manage all client subscriptions</p>
        </div>
      </div>

      {/* Stats */}
      <div className="adm-sub-stats-grid">
        <div className="adm-sub-stat-card adm-sub-stat-total">
          <div className="adm-sub-stat-label">Total Clients</div>
          <div className="adm-sub-stat-value">{stats.total}</div>
        </div>
        <div className="adm-sub-stat-card adm-sub-stat-active">
          <div className="adm-sub-stat-label">Active</div>
          <div className="adm-sub-stat-value">{stats.active}</div>
        </div>
        <div className="adm-sub-stat-card adm-sub-stat-expiring">
          <div className="adm-sub-stat-label">Expiring Soon</div>
          <div className="adm-sub-stat-value">{stats.expiring}</div>
        </div>
        <div className="adm-sub-stat-card adm-sub-stat-expired">
          <div className="adm-sub-stat-label">Expired</div>
          <div className="adm-sub-stat-value">{stats.expired}</div>
        </div>
        <div className="adm-sub-stat-card adm-sub-stat-none">
          <div className="adm-sub-stat-label">No Subscription</div>
          <div className="adm-sub-stat-value">{stats.noSub}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="adm-filter-row">
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`adm-filter-btn ${filter === f.key ? 'adm-filter-btn-active' : ''}`}
          >{f.label}</button>
        ))}
      </div>

      {/* Table */}
      <div className="adm-table-wrap">
        <table className="adm-table">
          <thead className="adm-table-head">
            <tr>
              <th className="adm-th">Organization</th>
              <th className="adm-th adm-th-center">Plan</th>
              <th className="adm-th adm-th-center">Price</th>
              <th className="adm-th adm-th-center">Status</th>
              <th className="adm-th adm-th-center">Validity</th>
              <th className="adm-th adm-th-center">Payment Date</th>
              <th className="adm-th adm-th-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredData().map((item, idx) => (
              <tr key={idx} className="adm-tr adm-tr-border">
                <td className="adm-td">
                  <div className="adm-client-name">{item.org_name}</div>
                  <div className="adm-client-email">{item.org_email}</div>
                </td>
                <td className="adm-td adm-td-center">
                  {item.plan_name
                    ? <span className="adm-badge adm-badge-progress">{item.plan_name}</span>
                    : <span className="adm-text-muted">No Plan</span>}
                </td>
                <td className="adm-td adm-td-center adm-td-price">
                  {item.plan_price ? `₹${parseInt(item.plan_price).toLocaleString()}` : '—'}
                </td>
                <td className="adm-td adm-td-center">
                  {getValidityBadge(item)}
                </td>
                <td className="adm-td adm-td-center adm-text-muted">
                  {item.end_date ? (
                    <>
                      <div>{new Date(item.end_date).toLocaleDateString()}</div>
                      {item.days_left !== null && (
                        <div className="adm-days-left">
                          {item.days_left > 0 ? `${Math.floor(item.days_left)} days left` : 'Expired'}
                        </div>
                      )}
                    </>
                  ) : '—'}
                </td>
                <td className="adm-td adm-td-center adm-text-muted">
                  {item.payment_date ? new Date(item.payment_date).toLocaleDateString() : '—'}
                </td>
                <td className="adm-td adm-td-center">
                  {item.subscription_id ? (
                    <button
                      onClick={() => handleDelete(item.subscription_id, item.org_name)}
                      disabled={deleting === item.subscription_id}
                      className={`adm-btn adm-btn-sm ${deleting === item.subscription_id ? 'adm-btn-disabled' : 'adm-btn-danger'}`}
                    >
                      {deleting === item.subscription_id ? '⏳ Deleting...' : '🗑️ Delete'}
                    </button>
                  ) : (
                    <span className="adm-text-muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredData().length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">📭</div>
          <div className="empty-state-title">No subscriptions found</div>
          <div className="empty-state-description">Try changing the filter</div>
        </div>
      )}
    </div>
  );
}

export default AdminSubscriptions;